import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { API, auth, inputCls } from '../finance/shared';
import { WhatsAppSettingsForm } from '../../../components/organization/WhatsAppSettingsForm';
import { VARIABLE_LABELS } from '../../../lib/whatsapp';
import Icon from '../../../components/dashboard/DashboardIcons';
import { toast } from '../../../lib/toast';

const EMPTY_FORM = { name: '', category: 'عام', body: '', variables: [] };

// «القوالب والإعدادات»: إدارة قوالب رسائل واتساب (إنشاء/تعديل/تعطيل/حذف/ترتيب) + إعدادات التواصل
export const TemplatesSettingsTab = () => {
  const [templates, setTemplates] = useState([]);
  const [availableVars, setAvailableVars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} | template
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/whatsapp/templates`, auth()).catch(() => ({ data: [] })),
      axios.get(`${API}/whatsapp/variables`, auth()).catch(() => ({ data: [] })),
    ]).then(([tRes, vRes]) => { setTemplates(tRes.data || []); setAvailableVars(vRes.data || []); })
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const openEdit = (t) => {
    setEditing(t || {});
    setForm(t ? { name: t.name, category: t.category, body: t.body, variables: t.variables || [] } : EMPTY_FORM);
  };

  const toggleVar = (v) => setForm((f) => ({ ...f, variables: f.variables.includes(v) ? f.variables.filter((x) => x !== v) : [...f.variables, v] }));
  const bodyRef = useRef(null);
  const cursorRef = useRef(null); // null = لم يتفاعل المستخدم مع الحقل بعد

  const trackCursor = () => {
    if (bodyRef.current) cursorRef.current = { start: bodyRef.current.selectionStart, end: bodyRef.current.selectionEnd };
  };

  const insertVariable = (v) => {
    const token = `{{${v}}}`;
    const text = form.body || '';
    const pos = cursorRef.current || { start: text.length, end: text.length };
    const start = Math.min(pos.start, text.length);
    const end = Math.min(pos.end, text.length);
    const newValue = text.slice(0, start) + token + text.slice(end);
    setForm((f) => ({ ...f, body: newValue }));
    toggleVar(v);
    toast.success(`تمت إضافة ${token}`);
    const newPos = start + token.length;
    cursorRef.current = { start: newPos, end: newPos };
    requestAnimationFrame(() => {
      if (bodyRef.current) {
        bodyRef.current.focus();
        bodyRef.current.setSelectionRange(newPos, newPos);
      }
    });
  };

  const save = async () => {
    if (!form.name.trim() || !form.body.trim()) { toast.error('الاسم ونص القالب مطلوبان'); return; }
    setSaving(true);
    try {
      if (editing?.id) await axios.put(`${API}/whatsapp/templates/${editing.id}`, form, auth());
      else await axios.post(`${API}/whatsapp/templates`, form, auth());
      toast.success('تم حفظ القالب');
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'تعذّر حفظ القالب');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (t) => {
    try { await axios.put(`${API}/whatsapp/templates/${t.id}`, { is_active: !t.is_active }, auth()); load(); }
    catch { toast.error('تعذّر التحديث'); }
  };

  const remove = async (t) => {
    if (!window.confirm(`حذف القالب "${t.name}"؟`)) return;
    try { await axios.delete(`${API}/whatsapp/templates/${t.id}`, auth()); load(); }
    catch { toast.error('تعذّر الحذف'); }
  };

  const move = async (index, dir) => {
    const next = [...templates];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setTemplates(next);
    try { await axios.post(`${API}/whatsapp/templates/reorder`, { ordered_ids: next.map((t) => t.id) }, auth()); }
    catch { toast.error('تعذّر حفظ الترتيب'); load(); }
  };

  return (
    <div className="space-y-6" data-testid="templates-settings-tab">
      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <span className="text-sm font-bold text-slate-800">قوالب الرسائل ({templates.length})</span>
          <button onClick={() => openEdit(null)} data-testid="template-add-btn" className="flex items-center gap-1.5 rounded-lg bg-[#16233b] px-3 py-1.5 text-xs font-bold text-white">
            <Icon name="plus" className="w-3.5 h-3.5" /> قالب جديد
          </button>
        </div>

        {loading ? (
          <div className="p-4 space-y-2">{[1, 2, 3].map((i) => <div key={i} className="h-10 animate-pulse rounded-lg bg-slate-100" />)}</div>
        ) : (
          <div className="divide-y divide-slate-50">
            {templates.map((t, idx) => (
              <div key={t.id} className={`flex items-center justify-between px-4 py-3 ${!t.is_active ? 'opacity-50' : ''}`} data-testid="template-row">
                <div>
                  <p className="text-sm font-bold text-slate-800">{t.name} {t.requires_financial && <span className="text-xs text-amber-600">(مالي)</span>}</p>
                  <p className="text-xs text-slate-400">{t.category}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => move(idx, -1)} data-testid="template-move-up-btn" className="rounded p-1.5 text-slate-400 hover:bg-slate-100"><Icon name="arrowUp" className="w-3.5 h-3.5" /></button>
                  <button onClick={() => move(idx, 1)} data-testid="template-move-down-btn" className="rounded p-1.5 text-slate-400 hover:bg-slate-100"><Icon name="arrowDown" className="w-3.5 h-3.5" /></button>
                  <button onClick={() => toggleActive(t)} data-testid="template-toggle-active-btn" className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600">{t.is_active ? 'تعطيل' : 'تفعيل'}</button>
                  <button onClick={() => openEdit(t)} data-testid="template-edit-btn" className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600">تعديل</button>
                  <button onClick={() => remove(t)} data-testid="template-delete-btn" className="rounded-lg border border-red-200 px-2.5 py-1 text-xs font-semibold text-red-500">حذف</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing !== null && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/50 p-4" dir="rtl" onClick={() => setEditing(null)} data-testid="template-edit-modal">
          <div className="w-full max-w-lg rounded-2xl bg-white p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="mb-4 text-base font-bold text-slate-800">{editing?.id ? 'تعديل القالب' : 'قالب جديد'}</h3>
            <div className="space-y-3">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="اسم القالب" data-testid="template-form-name" className={inputCls} />
              <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="التصنيف" data-testid="template-form-category" className={inputCls} />
              <textarea
                ref={bodyRef}
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                onSelect={trackCursor}
                onClick={trackCursor}
                onKeyUp={trackCursor}
                rows={6}
                placeholder="نص الرسالة، استخدم {{guardian_name}} مثلاً"
                data-testid="template-form-body"
                className={inputCls}
              />
              <div>
                <p className="mb-1.5 text-xs font-semibold text-slate-600">المتغيرات المتاحة لهذا القالب</p>
                <div className="flex flex-wrap gap-1.5">
                  {availableVars.map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => insertVariable(v)}
                      data-testid={`template-variable-chip-${v}`}
                      className={`rounded-full px-2.5 py-1 text-xs font-semibold ${form.variables.includes(v) ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600'}`}
                    >
                      {`{{${v}}}`} {VARIABLE_LABELS[v]}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-5 flex gap-2">
              <button onClick={save} disabled={saving} data-testid="template-form-save-btn" className="flex-1 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50">{saving ? 'جارٍ الحفظ...' : 'حفظ'}</button>
              <button onClick={() => setEditing(null)} data-testid="template-form-cancel-btn" className="rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600">إلغاء</button>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="mb-4 text-sm font-bold text-slate-800">إعدادات التواصل</h3>
        <WhatsAppSettingsForm />
      </div>
    </div>
  );
};
